import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";

const conflictSchema = z.object({
  response: z.object({
    errors: z.array(
      z.object({
        extensions: z.object({
          municipalityConflict: z.object({
            entityType: z.enum(["INVENTORY", "MUNICIPAL_PROPERTY_REGISTER"]),
            itemList: z.array(z.object({ id: z.string(), title: z.string() })),
            hasMore: z.boolean(),
            hasHidden: z.boolean(),
          }),
        }),
      }),
    ),
  }),
});

export function MunicipalPropertyMunicipalityConflictDialog({
  error,
  onClose,
}: {
  error: unknown;
  onClose: () => void;
}): ReactElement | null {
  const { t } = useTranslation("municipal-property");
  const parsed = conflictSchema.safeParse(error);
  if (!parsed.success) return null;
  const conflict = parsed.data.response.errors[0]?.extensions.municipalityConflict;
  if (!conflict) return null;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("municipalityConflict.title")}</DialogTitle>
          <DialogDescription>{t(`municipalityConflict.${conflict.entityType}`)}</DialogDescription>
        </DialogHeader>
        <ul className="max-h-64 list-disc overflow-y-auto pl-5 text-sm">
          {conflict.itemList.map((item) => (
            <li key={item.id}>{item.title}</li>
          ))}
        </ul>
        {conflict.hasHidden && <p className="text-sm text-muted-foreground">{t("municipalityConflict.hidden")}</p>}
        {conflict.hasMore && <p className="text-sm text-muted-foreground">{t("municipalityConflict.more")}</p>}
        <Button type="button" onClick={onClose}>
          {t("municipalityConflict.close")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
